/**
 * `/messages` — your inbox, your archive, and what you sent, as three tabs.
 *
 * Behind no permission: reading your own messages needs a session, like the
 * bell. Only "New message" asks for one, `message.send`, and the server asks
 * again when it is pressed.
 *
 * The tab lives in the URL (`?box=archived`), so Back returns to it and a link
 * to the archive is a link to the archive.
 */
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CreateIcon from '@mui/icons-material/Create';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { MESSAGING_PERMISSIONS, type MessageSummaryDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { UnreadDot } from '../../components/UnreadDot';
import { UnreadFilter } from '../../components/UnreadFilter';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { archive, listMessages, markRead, markUnread, unarchive, type MessageBox } from './api';
import { ComposeDialog } from './ComposeDialog';

const BOXES: readonly MessageBox[] = ['inbox', 'archived', 'sent'];

function boxFrom(value: string | null): MessageBox {
  return BOXES.find((box) => box === value) ?? 'inbox';
}

export default function MessagesPage() {
  const { t } = useTranslation(['messaging', 'common']);
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const box = boxFrom(params.get('box'));

  const [unreadOnly, setUnreadOnly] = useState(false);
  const [composing, setComposing] = useState(false);

  // Send order is the only sort the server offers — `id`, which is what the
  // "Sent" column's header toggles.
  const list = useListQuery({ defaultSort: 'id', defaultOrder: 'desc', resetKey: `${box}:${unreadOnly}` });
  const messages = useAsync(
    () => listMessages({ box, ...list.query, ...(box === 'inbox' && unreadOnly ? { unread: true } : {}) }),
    [box, list.key, unreadOnly],
  );

  const change = useSubmit(async (operation: (id: number) => Promise<unknown>, id: number) => {
    await operation(id);
    messages.reload();
  });

  const unread = messages.data?.unreadCount ?? 0;
  const sent = box === 'sent';

  function empty(): { title: string; description: string } {
    if (sent) return { title: t('empty.sent'), description: t('empty.sentDescription') };
    if (box === 'archived') return { title: t('empty.archived'), description: t('empty.archivedDescription') };
    if (unreadOnly) return { title: t('empty.unread'), description: t('empty.unreadDescription') };
    return { title: t('empty.inbox'), description: t('empty.inboxDescription') };
  }

  return (
    <>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          can(MESSAGING_PERMISSIONS.SEND) && (
            <Button variant="contained" startIcon={<CreateIcon />} onClick={() => setComposing(true)}>
              {t('action.compose')}
            </Button>
          )
        }
      />

      <Tabs
        value={box}
        onChange={(_event, value: MessageBox) => setParams(value === 'inbox' ? {} : { box: value })}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {BOXES.map((one) => (
          <Tab key={one} value={one} label={t(`box.${one}`)} />
        ))}
      </Tabs>

      {box === 'inbox' && (
        <UnreadFilter
          unreadOnly={unreadOnly}
          onChange={setUnreadOnly}
          unreadCount={unread}
          countLabel={t('unreadCount', { count: unread })}
        />
      )}

      {change.error && <ErrorState error={change.error} />}

      <AsyncContent state={messages}>
        {({ messages: rows, page }) => (
          <EntityTable<MessageSummaryDto>
            rows={rows}
            rowKey={(message) => message.id}
            list={list}
            page={page}
            empty={{ icon: <MailOutlinedIcon fontSize="inherit" />, ...empty() }}
            columns={[
              {
                field: 'subject',
                label: t('column.subject'),
                render: (message) => (
                  <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    <UnreadDot unread={!sent && message.readAt === null} />
                    <Link component={RouterLink} to={`/messages/${message.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                      {message.subject}
                    </Link>
                  </Stack>
                ),
              },
              sent
                ? {
                    field: 'to',
                    label: t('column.to'),
                    width: 180,
                    render: (message) => t('recipientCount', { count: message.recipientCount }),
                  }
                : {
                    field: 'from',
                    label: t('column.from'),
                    width: 220,
                    render: (message) => message.sender.displayName,
                  },
              {
                field: 'id',
                label: t('column.sent'),
                sortable: true,
                width: 170,
                render: (message) => <Timestamp value={message.sentAt} />,
              },
            ]}
            actions={(message) =>
              sent
                ? []
                : [
                    {
                      key: 'read',
                      label: message.readAt === null ? t('action.markRead') : t('action.markUnread'),
                      icon:
                        message.readAt === null ? (
                          <DraftsOutlinedIcon fontSize="small" />
                        ) : (
                          <MarkEmailUnreadOutlinedIcon fontSize="small" />
                        ),
                      disabled: change.pending,
                      onClick: () => void change.submit(message.readAt === null ? markRead : markUnread, message.id),
                    },
                    box === 'archived'
                      ? {
                          key: 'unarchive',
                          label: t('action.unarchive'),
                          icon: <UnarchiveOutlinedIcon fontSize="small" />,
                          disabled: change.pending,
                          onClick: () => void change.submit(unarchive, message.id),
                        }
                      : {
                          key: 'archive',
                          label: t('action.archive'),
                          icon: <ArchiveOutlinedIcon fontSize="small" />,
                          disabled: change.pending,
                          onClick: () => void change.submit(archive, message.id),
                        },
                  ]
            }
            actionsWidth={110}
          />
        )}
      </AsyncContent>

      <ComposeDialog
        open={composing}
        onClose={() => setComposing(false)}
        onSent={() => {
          // Show it where it now is. Already on Sent, the tab change is no
          // change, so reload instead.
          if (sent) messages.reload();
          else setParams({ box: 'sent' });
        }}
      />
    </>
  );
}
